import { useEffect, useState } from 'react'
import { validatePurchaseInput } from '../utils/partRequestPurchase'
import QuotationField from './QuotationField.jsx'
import UnitSelect from './UnitSelect.jsx'

function initialForm(request) {
  return {
    itemName: request?.itemName || request?.partName || '',
    manufacturer: request?.manufacturer || '',
    model: request?.model || '',
    specification: request?.specification || '',
    requestedQty: request?.requestedQty || 1,
    unit: request?.unit || '개',
    reason: request?.reason || '',
    purchaseUrl: request?.purchaseUrl || '',
    unitPrice: request?.unitPrice == null ? '' : String(request.unitPrice),
  }
}

export default function PartRequestEditModal({ open, request, onClose, onSubmit }) {
  const [form, setForm] = useState(() => initialForm(request))
  const [quotationFile, setQuotationFile] = useState(null)
  const [removeQuotation, setRemoveQuotation] = useState(false)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !request) return
    setForm(initialForm(request))
    setQuotationFile(null)
    setRemoveQuotation(false)
    setError('')
    setSaving(false)
  }, [open, request])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open || !request) return null

  function setField(key, value) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (!form.itemName.trim()) {
      setError('품명을 입력해 주세요.')
      return
    }
    const quantity = Number(form.requestedQty)
    if (!Number.isInteger(quantity) || quantity <= 0) {
      setError('요청수량은 1 이상이어야 합니다.')
      return
    }
    if (!form.reason.trim()) {
      setError('요청사유를 입력해 주세요.')
      return
    }
    const keepQuotation = Boolean(request.quotation) && !removeQuotation && !quotationFile
    const purchaseError = validatePurchaseInput({ ...form, file: quotationFile, keepQuotation })
    if (purchaseError) {
      setError(purchaseError)
      return
    }
    setSaving(true)
    setError('')
    try {
      await onSubmit(request.id, {
        itemName: form.itemName.trim(),
        manufacturer: form.manufacturer.trim(),
        model: form.model.trim(),
        specification: form.specification.trim(),
        requestedQty: quantity,
        unit: form.unit.trim() || '개',
        reason: form.reason.trim(),
        purchaseUrl: form.purchaseUrl.trim(),
        unitPrice: String(form.unitPrice).trim().replace(/,/g, ''),
        removeQuotation: removeQuotation && !quotationFile ? 'true' : 'false',
      }, quotationFile)
    } catch (err) {
      setError(err.message || '요청을 수정하지 못했습니다.')
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form
        className="modal-panel modal-panel--form"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        aria-labelledby="request-edit-title"
      >
        <h2 id="request-edit-title" className="modal-title">요청 수정</h2>
        <label className="field">
          <span>품목명</span>
          <input className="input" value={form.itemName} onChange={(event) => setField('itemName', event.target.value)} />
        </label>
        <label className="field">
          <span>제조사</span>
          <input className="input" value={form.manufacturer} onChange={(event) => setField('manufacturer', event.target.value)} />
        </label>
        <label className="field">
          <span>모델명</span>
          <input className="input" value={form.model} onChange={(event) => setField('model', event.target.value)} />
        </label>
        <label className="field">
          <span>사양</span>
          <textarea className="input input--area" value={form.specification} onChange={(event) => setField('specification', event.target.value)} />
        </label>
        <label className="field">
          <span>요청수량</span>
          <input
            className="input"
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            value={form.requestedQty}
            onChange={(event) => setField('requestedQty', event.target.value)}
          />
        </label>
        <label className="field">
          <span>단위</span>
          <UnitSelect value={form.unit} onChange={(unit) => setField('unit', unit)} />
        </label>
        <label className="field">
          <span>요청사유</span>
          <textarea className="input input--area" value={form.reason} onChange={(event) => setField('reason', event.target.value)} />
        </label>
        <label className="field">
          <span>구매처 링크</span>
          <input
            className="input"
            type="url"
            inputMode="url"
            value={form.purchaseUrl}
            onChange={(event) => setField('purchaseUrl', event.target.value)}
            placeholder="https://"
          />
        </label>
        <label className="field">
          <span>예상 단가(원) *</span>
          <input
            className="input"
            inputMode="numeric"
            value={form.unitPrice}
            onChange={(event) => setField('unitPrice', event.target.value)}
            placeholder="125000"
          />
        </label>
        <QuotationField
          file={quotationFile}
          existing={request.quotation}
          removed={removeQuotation}
          onFile={(next) => {
            setQuotationFile(next)
            if (next) setRemoveQuotation(false)
          }}
          onRemove={() => setRemoveQuotation(true)}
          onRestore={() => setRemoveQuotation(false)}
        />
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost btn--touch" onClick={onClose} disabled={saving}>취소</button>
          <button type="submit" className="btn btn--primary btn--touch" disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </form>
    </div>
  )
}
